import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import Button from '@dashboard/components/Button';
import { fetcher } from '@dashboard/services/request';
import { stringify } from 'query-string';
import { useMutation, useQuery } from '@tanstack/react-query';
import { appUrl } from '@dashboard/services/env';
import { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import { CheckIcon } from '@heroicons/react/24/outline';
import Combobox from '@dashboard/components/Form/components/Combobox';
import { ComboboxOption } from '@headlessui/react';
import clsx from 'clsx';
import { sellerSlackKey } from '@dashboard/lib/queryKeys';

export default function SlackForm({ integration, onSubmit }) {
  const { token, sellerId } = useAuth();
  const [newAccount, setNewAccount] = useState(null);
  const [error, setError] = useState(null);
  const [showChannelPicker, setShowChannelPicker] = useState(false);
  const hasAutoSubmitted = useRef(false);

  const { data: existingAccount, isLoading } = useQuery({
    queryKey: sellerSlackKey(sellerId),
    queryFn: () => fetcher(`/oauth/slack?sellerId=${sellerId}`, token),
    enabled: !!sellerId,
    retry: false,
  });

  const { data: channels, isLoading: isLoadingChannels } = useQuery({
    queryKey: ['slack-channels', sellerId],
    queryFn: () => fetcher(`/oauth/slack/channels`, token),
    enabled: !!sellerId && (!!existingAccount || !!newAccount),
    retry: false,
  });

  // Auto-create integration with OAuth channel when new account is created
  useEffect(() => {
    if (newAccount && !integration?._id && !hasAutoSubmitted.current) {
      const channelId = newAccount.config?.defaultChannelId;
      const channelName = newAccount.config?.defaultChannelName;

      if (channelId && channelName) {
        hasAutoSubmitted.current = true;
        // Auto-submit with the channel selected during OAuth
        onSubmit({
          config: {
            channelId,
            channelName,
          },
        });
      }
    }
  }, [newAccount, integration?._id, onSubmit]);

  if (isLoading) {
    return null;
  }

  const account = existingAccount || newAccount;

  // If no account connected, show auth flow
  if (!account) {
    return (
      <SlackAuthFlow onDone={account => setNewAccount(account)} error={error} setError={setError} />
    );
  }

  // If integration already exists, show connected state
  if (integration?._id) {
    if (showChannelPicker) {
      // Show channel picker form
      return (
        <ChannelPicker
          channels={channels}
          isLoadingChannels={isLoadingChannels}
          existingAccount={existingAccount}
          initialChannel={integration.config}
          onSubmit={channelData => {
            onSubmit({
              config: channelData,
            });
            setShowChannelPicker(false);
          }}
          onCancel={() => setShowChannelPicker(false)}
        />
      );
    }

    return (
      <div className="space-y-2">
        <div className="text-sm text-gray-600 dark:text-muted-foreground">
          {existingAccount?.config?.teamName ? (
            <div className="flex gap-x-1">
              <CheckIcon className="inline-block h-5 w-5 text-green-600 dark:text-green-500" />
              <div>
                Connected to: <span className="font-medium">{existingAccount.config.teamName}</span>
              </div>
            </div>
          ) : (
            <div className="flex gap-x-1">
              <CheckIcon className="inline-block h-5 w-5 text-green-600 dark:text-green-500" />
              <div>Connected to Slack</div>
            </div>
          )}
        </div>

        <div className="text-sm text-gray-600 dark:text-muted-foreground">
          Sending notifications to{' '}
          <span className="font-medium">
            {integration.config.channelName?.startsWith('#')
              ? integration.config.channelName
              : `#${integration.config.channelName}`}
          </span>
        </div>

        <div className="flex gap-x-2">
          <Button appearance="secondary" size="small" onClick={() => setShowChannelPicker(true)}>
            Change channel
          </Button>
          <Button appearance="secondary" size="small" href="/settings/integrations">
            Change Slack workspace
          </Button>
        </div>
      </div>
    );
  }

  // This shouldn't be reached since we auto-create, but keep as fallback
  return (
    <ChannelPicker
      channels={channels}
      isLoadingChannels={isLoadingChannels}
      existingAccount={existingAccount}
      initialChannel={integration?.config}
      onSubmit={channelData => {
        onSubmit({
          config: channelData,
        });
      }}
      submitLabel="Add integration"
    />
  );
}

function ChannelPicker({
  channels,
  isLoadingChannels,
  existingAccount,
  initialChannel,
  onSubmit,
  onCancel,
  submitLabel = 'Save channel',
}) {
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [query, setQuery] = useState('');
  const [isShowingChannelPicker, setIsShowingChannelPicker] = useState(false);

  // Set initial channel if provided
  useEffect(() => {
    if (initialChannel?.channelId && channels) {
      const channel = channels.find(c => c.id === initialChannel.channelId);
      if (channel) {
        setSelectedChannel({
          _id: channel.id,
          name: channel.name,
          isPrivate: channel.isPrivate,
        });
      }
    }
  }, [initialChannel, channels]);

  const filteredChannels =
    query === ''
      ? channels
      : channels?.filter(channel => channel.name.toLowerCase().includes(query.toLowerCase()));

  const handleSubmit = () => {
    if (!selectedChannel) return;

    onSubmit({
      channelId: selectedChannel._id,
      channelName: selectedChannel.name,
    });
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600 dark:text-muted-foreground">
        {existingAccount?.config?.teamName ? (
          <div className="flex gap-x-1">
            <CheckIcon className="inline-block h-5 w-5 text-green-600 dark:text-green-500" />
            <div>
              Connected to: <span className="font-medium">{existingAccount.config.teamName}</span>
            </div>
          </div>
        ) : (
          <div className="flex gap-x-1">
            <CheckIcon className="inline-block h-5 w-5 text-green-600 dark:text-green-500" />
            <div>Connected to Slack</div>
          </div>
        )}
      </div>

      <div className={clsx({ 'mb-64': isShowingChannelPicker })}>
        <Combobox
          name="channelId"
          immediate
          value={selectedChannel}
          onChange={value => {
            if (value == null) {
              setSelectedChannel(null);
              setQuery('');
              return;
            }
            setQuery(value.name);
            setSelectedChannel(value);
          }}
          onClear={e => {
            e.preventDefault();
            setSelectedChannel(null);
            setQuery('');
          }}
          onChangeInput={e => setQuery(e.target.value)}
          placeholder="Search for a channel..."
          hasItems={filteredChannels?.length > 0}
          isLoading={isLoadingChannels}
          noItemsMsg="No channels found"
          fetchNextPage={() => {}}
          label="Select Slack channel"
          tooltip="Choose which channel to send notifications to"
          showLabel={true}
          onFocus={() => setIsShowingChannelPicker(true)}
          onBlur={() => setIsShowingChannelPicker(false)}
        >
          {filteredChannels?.map(channel => (
            <ComboboxOption
              key={channel.id}
              value={{ _id: channel.id, name: channel.name, isPrivate: channel.isPrivate }}
              className={({ focus }) =>
                `cursor-pointer relative ${focus ? 'bg-gray-100 dark:bg-muted' : ''}`
              }
            >
              <div
                className={clsx(
                  'flex justify-start items-center w-full text-left p-2 hover:bg-gray-100 dark:hover:bg-muted transition-colors border-b border-gray-100 dark:border-border',
                  {
                    'bg-gray-50 dark:bg-card': channel.id === selectedChannel?._id,
                  },
                )}
              >
                <div className="flex-1">
                  <div className="font-medium">
                    # {channel.name}
                    {channel.isPrivate && (
                      <span className="text-gray-500 dark:text-muted-foreground ml-1">
                        (private)
                      </span>
                    )}
                  </div>
                </div>
                {channel.id === selectedChannel?._id && (
                  <div>
                    <CheckIcon className="w-5 h-5 text-gray-500 dark:text-muted-foreground" />
                  </div>
                )}
              </div>
            </ComboboxOption>
          ))}
        </Combobox>
      </div>

      <div className="flex gap-x-2">
        <Button appearance="primary" onClick={handleSubmit} disabled={!selectedChannel}>
          {submitLabel}
        </Button>
        {onCancel && (
          <Button appearance="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}

function SlackAuthFlow({ onDone, error, setError }) {
  const { token, sellerId } = useAuth();

  const createOAuthMutation = useMutation({
    mutationFn: code => {
      return fetcher(`/oauth/slack`, token, {
        method: 'POST',
        body: JSON.stringify({
          code,
          sellerId: sellerId,
          redirectURI: `${appUrl}/dashboard/integrations/slack/callback`,
        }),
      });
    },
    onSuccess: data => {
      onDone(data);
    },
    onError: error => {
      setError(error.message);
    },
  });

  function connectSlack() {
    const endpointUrl = `https://slack.com/oauth/v2/authorize?${stringify({
      client_id: process.env.REACT_APP_SLACK_CLIENT_ID,
      redirect_uri: `${appUrl}/dashboard/integrations/slack/callback`,
      scope: ['incoming-webhook', 'chat:write', 'channels:read', 'groups:read'].join(','),
    })}`;

    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2.5;

    window.open(
      endpointUrl,
      'popup',
      `popup=true,width=${width},height=${height},left=${left},top=${top}`,
    );
  }

  useEffect(() => {
    const messageHandler = event => {
      if (event.origin !== appUrl) return;

      const { type, code } = event.data;

      if (type === 'slack-oauth-callback' && code) {
        createOAuthMutation.mutate(code);
      }
    };

    window.addEventListener('message', messageHandler);

    return () => window.removeEventListener('message', messageHandler);
  }, []);

  return (
    <>
      <Button onClick={connectSlack} appearance="primary">
        Connect with Slack
      </Button>

      {error && (
        <div className="mt-4">
          <FormErrorBase>{error}</FormErrorBase>
        </div>
      )}
    </>
  );
}
